import { action, computed, observable } from 'mobx';

import { AppManager, Variables } from 'core';
import { analytics } from 'core/analytics';

import { FileModel, FileApp } from 'apps/file/app';
import {
  FileQuery,
  FileConversionPolling,
  ConversionProgressSubscription,
} from 'apps/file/gql';
import {
  ContributionFileDetailsQuery,
  ContributionFileVersionDetailsQuery,
} from 'apps/repository/gql';

import {
  ContributionFileModel,
  ContributionFileApp,
  OperationModel,
  OperationApp,
  RepositoryItem,
} from 'apps/repository/app';
import { ProjectContextModel, ProjectContextApp } from 'apps/context/app';
import { frameStore } from 'apps/components/Frame/appManager';

// Because fileId passes through decodeURIComponent, empty content needs to be filtered
const nilStringList = ['null', 'undefined'];

export class FileViewerManager extends AppManager {
  @observable hasError = false;
  @observable filename = '';
  @observable contribFile = null;
  @observable conversionProgress = '';
  @observable conversionSteps = '';

  constructor(parent, init = {}) {
    super(parent, init);
    this.AppRef('file', FileApp);
    this.AppRef('contribFile', ContributionFileApp);
    this.AppRef('operation', OperationApp);
    this.AppRef('contextApp', ProjectContextApp);
    this.contribFile = null;
    if (this.fileID && !nilStringList.includes(this.fileID)) {
      this.variables = new Variables({
        id: () => this.fileID,
      });

      this.variables.onChange(['id'], this.FileDetailsQuery);
    } else if (this.slug && this.basepath) {
      this.variables = new Variables({
        space: () => this.profile || this.initiative,
        slug: () => this.slug,
        filepath: () => this.basepath || '',
        version: () => this.contributionId || '',
      });

      this.variables.onChange(
        ['space', 'slug', 'filepath', 'version'],
        this.ContributionFileQueryWithConversion,
      );
    }
  }

  @computed get loading() {
    const fileDetailsQuery = this.queries.get('fileDetails');
    return fileDetailsQuery?.loading;
  }

  @computed get repositoryItem() {
    if (!this.contribFile && !this.operation) return null;
    return new RepositoryItem({
      contributionFile: this.contribFile,
      operation: this.operation,
    });
  }

  @computed get atHead() {
    return !this.variables.version;
  }

  @action CatchError = error => {
    this.hasError = true;
    analytics.logError(error);
  };

  @action handlePollProgress = fileModel => {
    this.handleConversionProgress(fileModel?.conversionGltfjson);
    if (fileModel?.conversionGltfjson?.status === 'SUCCESS') {
      this.fileConversionPollingQuery?.disposer();
      this.fileConversionPollingQuery = null;
    }
  };

  @action FileConversionPollingQuery = () => {
    if (this.fileConversionPollingQuery) return;
    this.fileConversionPollingQuery = new FileConversionPolling(this, {
      queryKey: 'fileConversionPolling',
      onSysErr: this.onError,
      pollInterval: 10000,
    });
    this.fileConversionPollingQuery.process(
      fileData => FileModel.Load(fileData),
      this.handlePollProgress,
    );
    this.fileConversionPollingQuery.query();
  };

  @action FileDetailsQuery = () => {
    this.fileQuery = new FileQuery(this, {
      queryKey: 'fileDetails',
      onSysErr: this.onError,
      fetchPolicy: 'no-cache',
    });
    this.fileQuery.process(
      fileDetails => fileDetails && FileModel.Load(fileDetails),
      this.FileUpdate,
    );
    this.fileQuery.process(
      contextObj => contextObj && ProjectContextModel.Load(contextObj),
      this.UpdateContext,
      { subPath: 'context' },
    );
    this.fileQuery.query();
  };

  @action handleConversionProgress = result => {
    if (!(this.file && result)) return;
    if (!this.file.conversionGltfjson) {
      this.file.conversionGltfjson = { status: 'PENDING' };
    }
    this.file.conversionGltfjson.status = result.status;
    this.file.conversionGltfjson.reason = result.reason;
    this.file.conversionGltfjson.errorCode = result.errorCode;
    if (result.progress !== '') {
      this.conversionProgress = result.progress;
    }
    if (result.steps !== '') {
      this.conversionSteps = result.steps;
    }
    if (
      result.status === 'SUCCESS' ||
      result.status === 'ERROR' ||
      result.status === 'PDF'
    ) {
      this.conversionSubscription?.CompleteSubscription();
    }
  };

  async SubscribeConversionProgress() {
    if (this.conversionSubscription) {
      return;
    }

    this.InitiateSubscription();
  }

  @action InitiateSubscription = () => {
    if (!this.contextApp?.id) {
      return;
    }
    if (!this.file?.id) {
      return;
    }
    const variables = {
      subscribeFileProcessInput: {
        fileId: this.file.id,
        conversionType: 'gltf',
        contextId: this.contextApp?.id,
      },
    };

    this.conversionSubscription = new ConversionProgressSubscription(this, {
      variables,
    });

    this.conversionSubscription.process(this.handleConversionProgress);
    this.conversionSubscription.StartSubscription();

    // - why are we doing this? I think this is wrong and serves no
    // purpose, it re-triggers the queries after we subscribe, but then
    // we're already waiting for an update?
    // if (this.fileID) {
    //   this.FileConversionPollingQuery();
    // } else if (this.slug && this.basepath) {
    //   this.ContributionFileQuery();
    // }
  };

  @action ContributionFileQueryWithConversion = () => {
    this.ContributionFileQuery(true);
  };

  @action ContributionFileQuery = allowConversion => {
    const variables = new Variables({
      space: () => this.profile || this.initiative,
      slug: () => this.slug,
      filepath: () => this.basepath || '',
      version: () => this.contributionId || '',
      conversionSpec: () => (allowConversion ? GLTF_VERSION || 'latest' : null),
    });
    const Query = !this.atHead
      ? ContributionFileVersionDetailsQuery
      : ContributionFileDetailsQuery;
    const subPath = !this.atHead
      ? 'contribution.contribFile'
      : 'contributionUpstream.contribFile';
    this.fileQuery = new Query(this, {
      queryKey: 'fileDetails',
      onSysErr: this.onError,
      fetchPolicy: 'no-cache',
      variables,
    });
    this.fileQuery.process(project => {
      if (project) {
        frameStore.SetProject(project);
      }
    });
    this.fileQuery.process(
      contribFileDetails =>
        contribFileDetails && ContributionFileModel.Load(contribFileDetails),
      this.ContributionFileUpdate,
      { subPath },
    );
    this.fileQuery.process(
      pendingOperations =>
        pendingOperations &&
        OperationModel.Load(
          pendingOperations.find(op => op.path === this.variables.filepath) ||
            null,
        ),
      this.PendingOperationUpdate,
      { subPath: 'pendingOperations' },
    );
    this.fileQuery.process(
      contextObj => contextObj && ProjectContextModel.Load(contextObj),
      this.UpdateContext,
      { subPath: 'context' },
    );
    this.fileQuery.query();
  };

  @action ContributionFileUpdate = contributionFile => {
    if (!contributionFile?.file) return;
    this.contribFile = contributionFile;
    this.file = contributionFile.file;
    this.filename = contributionFile.filename;
    this.hasError = false;
    const conversion = contributionFile.file.conversionGltfjson;
    if (!conversion || conversion.status === 'PENDING') {
      this.SubscribeConversionProgress();
    }
  };

  @action FileUpdate = file => {
    if (!file) return;
    this.contribFile = null;
    this.file = file;
    this.filename = file.filename;
    this.hasError = false;
    const conversion = file.conversionGltfjson;
    if (!conversion || conversion.status === 'PENDING') {
      this.SubscribeConversionProgress();
    }
  };

  @action UpdateContext = context => {
    if (context) {
      this.contextApp = context;
      const conversion = this.file?.conversionGltfjson;
      if (!conversion || conversion.status === 'PENDING') {
        this.SubscribeConversionProgress();
      }
    }
  };

  @action PendingOperationUpdate = operation => {
    if (!operation) return;
    this.operation = operation;
    this.file = operation.file;
    this.hasError = false;
    const conversion = operation.file.conversionGltfjson;
    if (!conversion || conversion.status === 'PENDING') {
      this.SubscribeConversionProgress();
    }
  };
}
